import type mapboxgl from 'mapbox-gl';
import { fetchAvailabilitySnapshot } from './api.js';
import { ensureMapReady, getApiKeyOrLog } from './context.js';
import { clearOverlay, updateOverlay } from './mapResources.js';
import { buildRoomFeatures } from './roomFeatures.js';
import { loadRoomsWithGeometry } from './roomGeometry.js';
import { selectRoomsOrWarn } from './roomSelection.js';
import type { FetcherContext, RoomFeatureCollection } from './types.js';

export const fetchMeetingRoomData = async (context: FetcherContext) => {
  const meetingRoomAvailabilityApiKey = getApiKeyOrLog(context);
  if (!meetingRoomAvailabilityApiKey) {
    return;
  }

  let mapboxMap: mapboxgl.Map | null = null;

  try {
    mapboxMap = await ensureMapReady(context);
    if (!mapboxMap) {
      return;
    }

    const availability = await fetchAvailabilitySnapshot(
      meetingRoomAvailabilityApiKey,
      context.deps.building,
    );
    if (availability.size === 0) {
      clearOverlay(mapboxMap, context);
      return;
    }
    const rooms = await selectRoomsOrWarn(context);
    if (!rooms) {
      clearOverlay(mapboxMap, context);
      return;
    }

    const roomsWithGeometry = await loadRoomsWithGeometry(rooms, context);
    if (roomsWithGeometry.length === 0) {
      clearOverlay(mapboxMap, context);
      return;
    }

    const { features, statusesChanged } = buildRoomFeatures(
      roomsWithGeometry,
      availability,
      context,
    );
    if (features.length === 0) {
      clearOverlay(mapboxMap, context);
      return;
    }

    const featureCollection: RoomFeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    await updateOverlay(mapboxMap, featureCollection, statusesChanged, context);
  } catch (error) {
    if (mapboxMap) {
      clearOverlay(mapboxMap, context);
    }

    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error(error);
    }
  }
};
